import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import styled from 'styled-components';
import client, { mensajeDeError } from '../../api/client';
import Card from '../../components/ui/Card';
import Badge from '../../components/ui/Badge';
import Button from '../../components/ui/Button';
import Spinner from '../../components/ui/Spinner';
import QrScanner from '../../components/QrScanner';
import { MensajeError, MensajeExito } from '../../components/ui/Form';
import { fechaCompleta, horaLegible } from '../../utils/fechas';

const Pagina = styled.div`
  min-height: 100vh;
  background: ${({ theme }) => theme.colors.bg};
  padding: ${({ theme }) => theme.space.lg} ${({ theme }) => theme.space.md};
`;

const Contenido = styled.div`
  max-width: 480px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space.lg};
`;

const Marca = styled.p`
  text-align: center;
  font-family: ${({ theme }) => theme.fonts.heading};
  color: ${({ theme }) => theme.colors.sageDeep};
  font-size: 1.1rem;
`;

const Cabecera = styled(Card)`
  text-align: center;

  h1 {
    font-size: 1.3rem;
    color: ${({ theme }) => theme.colors.sageDeep};
    margin-bottom: ${({ theme }) => theme.space.xs};
  }

  p {
    font-size: 0.9rem;
    color: ${({ theme }) => theme.colors.textMuted};
    margin-bottom: ${({ theme }) => theme.space.sm};
  }
`;

const Insignias = styled.div`
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.space.xs};
`;

const PanelScanner = styled(Card)`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme }) => theme.space.sm};

  h2 {
    font-size: 1rem;
    color: ${({ theme }) => theme.colors.sageDeep};
    align-self: flex-start;
  }
`;

const Roster = styled(Card)`
  padding: 0;
  overflow: hidden;

  h2 {
    font-size: 1rem;
    padding: ${({ theme }) => theme.space.md};
    background: ${({ theme }) => theme.colors.sand};
    color: ${({ theme }) => theme.colors.sageDeep};
  }
`;

const Cliente = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${({ theme }) => theme.space.sm};
  padding: 0.7rem ${({ theme }) => theme.space.md};
  border-top: 1px solid ${({ theme }) => theme.colors.border};

  strong {
    font-weight: 600;
  }

  span {
    font-size: 0.85rem;
    color: ${({ theme }) => theme.colors.textMuted};
    margin-right: auto;
  }
`;

const SinClientes = styled.p`
  padding: ${({ theme }) => theme.space.lg};
  text-align: center;
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: 0.9rem;
`;

const VolverLink = styled(Link)`
  display: block;
  text-align: center;
  font-size: 0.85rem;
  color: ${({ theme }) => theme.colors.textMuted};
  text-decoration: underline;
`;

function CheckinClase() {
  const { claseId } = useParams();
  const [clase, setClase] = useState(null);
  const [clientes, setClientes] = useState(null);
  const [error, setError] = useState('');
  const [checkinError, setCheckinError] = useState('');
  const [checkinExito, setCheckinExito] = useState('');
  const [registrando, setRegistrando] = useState(false);
  const registrandoRef = useRef(false);

  useEffect(() => {
    client
      .get(`/clases/${claseId}`)
      .then(({ data }) => setClase(data))
      .catch(() => setError('No se encontró la clase.'));
  }, [claseId]);

  const cargarClientes = useCallback(() => {
    if (!clase) return;
    client
      .get('/reservas', { params: { desde: clase.fecha, hasta: clase.fecha } })
      .then(({ data }) => setClientes(data.filter((r) => String(r.clase_id) === String(claseId))))
      .catch(() => setClientes([]));
  }, [clase, claseId]);

  useEffect(() => {
    cargarClientes();
  }, [cargarClientes]);

  const marcar = async (reservaId, estado) => {
    try {
      await client.patch(`/reservas/${reservaId}/asistencia`, { estado });
      cargarClientes();
    } catch (err) {
      setCheckinError(mensajeDeError(err));
    }
  };

  const hacerCheckin = useCallback(
    async (codigo) => {
      if (!codigo || registrandoRef.current) return;
      registrandoRef.current = true;
      setRegistrando(true);
      setCheckinError('');
      setCheckinExito('');
      try {
        const { data } = await client.post('/reservas/checkin', { token: codigo, claseId });
        setCheckinExito(data.mensaje);
        cargarClientes();
      } catch (err) {
        setCheckinError(mensajeDeError(err));
      } finally {
        registrandoRef.current = false;
        setRegistrando(false);
      }
    },
    [claseId, cargarClientes]
  );

  if (error) {
    return (
      <Pagina>
        <Contenido>
          <MensajeError role="alert">{error}</MensajeError>
        </Contenido>
      </Pagina>
    );
  }

  if (!clase) {
    return (
      <Pagina>
        <Spinner />
      </Pagina>
    );
  }

  return (
    <Pagina>
      <Contenido>
        <Marca>numa. check-in</Marca>

        <Cabecera>
          <h1>{clase.tipo_clase}</h1>
          <p>
            {fechaCompleta(clase.fecha)} · {horaLegible(clase.hora_inicio)} · {clase.instructor}
          </p>
          <Insignias>
            <Badge $tono="arena">{clase.duracion_minutos} min</Badge>
            <Badge $tono={clase.cupo_disponible === 0 ? 'peligro' : 'exito'}>
              {clase.cupo_maximo - clase.cupo_disponible}/{clase.cupo_maximo} lugares
            </Badge>
          </Insignias>
        </Cabecera>

        <PanelScanner>
          <h2>Escanear entrada</h2>
          {checkinError && <MensajeError role="alert">{checkinError}</MensajeError>}
          {checkinExito && <MensajeExito role="status">{checkinExito}</MensajeExito>}
          <QrScanner onDetectado={hacerCheckin} deshabilitado={registrando} />
        </PanelScanner>

        <Roster>
          <h2>Clientes de esta clase</h2>
          {!clientes ? (
            <Spinner />
          ) : clientes.length === 0 ? (
            <SinClientes>Nadie ha reservado esta clase todavía.</SinClientes>
          ) : (
            clientes.map((r) => (
              <Cliente key={r.id}>
                <strong>{r.cliente}</strong>
                <span>{r.telefono || r.email}</span>
                {r.estado === 'asistio' && <Badge $tono="exito">Asistió</Badge>}
                {r.estado === 'falta' && <Badge $tono="peligro">Faltó</Badge>}
                {r.estado === 'reservada' ? (
                  <>
                    <Button $variante="secundario" $chico onClick={() => marcar(r.id, 'asistio')}>
                      Asistió
                    </Button>
                    <Button $variante="peligro" $chico onClick={() => marcar(r.id, 'falta')}>
                      Faltó
                    </Button>
                  </>
                ) : (
                  <Button $variante="fantasma" $chico onClick={() => marcar(r.id, 'reservada')}>
                    Deshacer
                  </Button>
                )}
              </Cliente>
            ))
          )}
        </Roster>

        <VolverLink to="/admin/clases">Volver a clases</VolverLink>
      </Contenido>
    </Pagina>
  );
}

export default CheckinClase;
